/**
 * Proposal editor — the whole sales document in one form.
 *
 * Sections rendered (top-to-bottom matching the sample proposal):
 *   1. Header: title / prepared-for / prepared-by / valid-until / status
 *   2. Long-form intro + objectives
 *   3. Domain options table (with availability lookup)
 *   4. Every ProposalSection (rendered as an editable line-item table)
 *   5. Inclusions/exclusions, payment terms, compliance, expected outcome
 *   6. Totals strip (one-off + monthly + first-year)
 *   7. Send dialog (compose intro email, choose recipients, ship via SendGrid)
 *
 * All edits POST PUT /api/proposals/:id with the full document. The backend
 * recomputes totals every save so the FE doesn't have to.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Save, Send, FileText, Plus, Trash2, Loader2, ArrowLeft, Globe,
  Search, Star, ExternalLink, GripVertical, Lock, Unlink, Copy, EyeOff,
  Pencil, CornerDownRight,
} from "lucide-react";
import { SortableList, SortableItem } from "@/components/Sortable";

// Strip transient `_uid` fields stamped on domains/competitors so
// dnd-kit has stable React-side IDs across renders. These never get
// persisted to the backend.
function _stripUids(arr) {
  return (arr || []).map((o) => {
    if (o && typeof o === "object" && "_uid" in o) {
      // Drop the transient field; nothing else changes.
      const rest = { ...o };
      delete rest._uid;
      return rest;
    }
    return o;
  });
}

function _genUid(prefix = "u") {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

// Stamp _uid on every entry that doesn't already have one. Idempotent.
function _stampUids(arr, prefix = "u") {
  return (arr || []).map((o) =>
    o && typeof o === "object" && o._uid ? o : { ...o, _uid: _genUid(prefix) },
  );
}

// search_terms is List[str] on the wire — wrap into {value,_uid} for
// the editor so we can drag a row whose `value` is currently empty.
function _wrapTerms(strings) {
  return (strings || []).map((s) => ({ value: s || "", _uid: _genUid("t") }));
}
function _unwrapTerms(rows) {
  return (rows || []).map((r) =>
    (typeof r === "string" ? r : (r.value || "")).trim(),
  ).filter(Boolean);
}

function fmtMoney(v, cur = "AUD") {
  const sym = { AUD: "$", USD: "$", GBP: "£", EUR: "€", NZD: "$" }[cur] || "";
  // Match the proposal output — whole dollars only, no cents.
  return `${sym}${Math.round(Number(v || 0)).toLocaleString()}`;
}

// ── Choice-group helpers ───────────────────────────────────────────────
// Items sharing the same non-empty `choice_group` inside a section render
// as a single-select radio block on the client share view. The editor
// uses HTML5 drag-and-drop to assign groups: drop one item onto another
// to merge them into a group; drop onto empty space to clear.
const GROUP_PALETTE = [
  { bg: "bg-rose-100",    fg: "text-rose-800",    border: "border-l-rose-500" },
  { bg: "bg-amber-100",   fg: "text-amber-800",   border: "border-l-amber-500" },
  { bg: "bg-emerald-100", fg: "text-emerald-800", border: "border-l-emerald-500" },
  { bg: "bg-sky-100",     fg: "text-sky-800",     border: "border-l-sky-500" },
  { bg: "bg-violet-100",  fg: "text-violet-800",  border: "border-l-violet-500" },
  { bg: "bg-orange-100",  fg: "text-orange-800",  border: "border-l-orange-500" },
];

function groupPalette(section, key) {
  if (!key) return null;
  // Stable order based on first appearance inside this section so the
  // colour doesn't jump around when items are reordered.
  const order = [];
  (section.items || []).forEach((it) => {
    if (it.choice_group && !order.includes(it.choice_group)) {
      order.push(it.choice_group);
    }
  });
  const idx = order.indexOf(key);
  return GROUP_PALETTE[(idx >= 0 ? idx : 0) % GROUP_PALETTE.length];
}

function groupLabel(section, key) {
  if (!key) return "";
  const order = [];
  (section.items || []).forEach((it) => {
    if (it.choice_group && !order.includes(it.choice_group)) {
      order.push(it.choice_group);
    }
  });
  const idx = order.indexOf(key);
  return `Choice ${String.fromCharCode(65 + (idx >= 0 ? idx : 0))}`;
}

/* ── ProposalTasksSection ────────────────────────────────────────
 * Compact task list rendered at the bottom of the proposal editor.
 * Tasks live in the standard `tasks` collection (proposal_id set) so
 * they'll automatically activate into the project the moment the
 * proposal is accepted (see `activate_proposal` on the backend).
 * Supports inline editing (title/desc/priority/hours) and unlimited
 * cascading subtasks via `parent_task_id` (the backend already
 * threads the tree — we just render + surface the actions).
 * ---------------------------------------------------------------- */
function ProposalTasksSection({ proposalId, proposalStatus }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState("");
  const [newPriority, setNewPriority] = useState("normal");
  const [newHours, setNewHours] = useState("");
  const [adding, setAdding] = useState(false);
  // Per-row inline edit state — keyed by task_id. `null` = not editing.
  const [editing, setEditing] = useState({});
  // Per-parent subtask draft — {parent_id: {title, priority, hours, open}}
  const [subDraft, setSubDraft] = useState({});

  const load = useCallback(async () => {
    if (!proposalId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/tasks",
        { params: { proposal_id: proposalId } });
      setTasks(data.tasks || data || []);
    } catch (e) {
      console.warn("Proposal tasks load failed", e);
    } finally { setLoading(false); }
  }, [proposalId]);

  useEffect(() => { load(); }, [load]);

  const addTask = async (parentTaskId = null, draft = null) => {
    const src = draft || {
      title: newTitle.trim(),
      priority: newPriority,
      hours: newHours,
    };
    if (!src.title) return;
    setAdding(true);
    try {
      await api.post("/tasks", {
        proposal_id: proposalId,
        parent_task_id: parentTaskId,
        title: src.title,
        priority: src.priority || "normal",
        estimate_hours: src.hours ? Number(src.hours) : null,
      });
      if (parentTaskId) {
        setSubDraft((prev) => ({ ...prev,
          [parentTaskId]: { open: false, title: "", priority: "normal", hours: "" } }));
      } else {
        setNewTitle(""); setNewHours(""); setNewPriority("normal");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add task failed");
    } finally { setAdding(false); }
  };

  const removeTask = async (id) => {
    if (!window.confirm(
        "Delete this task? Subtasks under it will also be deleted.")) return;
    try {
      await api.delete(`/tasks/${id}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const startEdit = (t) => setEditing((prev) => ({
    ...prev,
    [t.task_id]: {
      title: t.title || "",
      description: t.description || "",
      priority: t.priority || "normal",
      estimate_hours: t.estimate_hours ?? "",
    },
  }));
  const cancelEdit = (id) => setEditing((prev) => {
    const n = { ...prev }; delete n[id]; return n;
  });
  const saveEdit = async (id) => {
    const draft = editing[id];
    if (!draft || !draft.title.trim()) return;
    try {
      await api.patch(`/tasks/${id}`, {
        title: draft.title.trim(),
        description: draft.description.trim() || null,
        priority: draft.priority,
        estimate_hours: draft.estimate_hours === "" ? null : Number(draft.estimate_hours),
      });
      cancelEdit(id);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const toggleSubtask = (parentId) => setSubDraft((prev) => ({
    ...prev,
    [parentId]: {
      open: !prev[parentId]?.open,
      title: prev[parentId]?.title || "",
      priority: prev[parentId]?.priority || "normal",
      hours: prev[parentId]?.hours || "",
    },
  }));
  const setSubField = (parentId, field, value) => setSubDraft((prev) => ({
    ...prev,
    [parentId]: { ...(prev[parentId] || { open: true }), [field]: value },
  }));

  const isAccepted = proposalStatus === "accepted";
  const priorityColors = {
    low: "bg-zinc-100 text-zinc-600 border-zinc-200",
    normal: "bg-sky-100 text-sky-800 border-sky-200",
    high: "bg-amber-100 text-amber-800 border-amber-200",
    urgent: "bg-red-100 text-red-800 border-red-200",
  };

  // Build parent → children map so we can render the tree without
  // recursion (nested React fragments crash the visual-edits babel
  // plugin — same reason `TaskTree` on the Task Manager page renders
  // flat with depth-indent).
  const roots = tasks.filter((t) => !t.parent_task_id);
  const childrenByParent = {};
  tasks.forEach((t) => {
    if (t.parent_task_id) {
      (childrenByParent[t.parent_task_id] ||= []).push(t);
    }
  });
  const flatOrdered = [];
  const walk = (task, depth) => {
    flatOrdered.push({ task, depth });
    (childrenByParent[task.task_id] || []).forEach((c) => walk(c, depth + 1));
  };
  roots.forEach((r) => walk(r, 0));

  const renderRow = ({ task: t, depth }, i) => {
    const isEditing = !!editing[t.task_id];
    const draft = editing[t.task_id];
    const sub = subDraft[t.task_id];
    return (
      <React.Fragment key={t.task_id}>
        <div
          className="flex items-center gap-2 py-2 border-b border-zinc-100 last:border-b-0"
          style={{ paddingLeft: depth * 24 }}
          data-testid={`proposal-task-row-${t.task_id}`}>
          {depth > 0 && (
            <CornerDownRight className="h-3 w-3 text-zinc-300 shrink-0" />
          )}
          {depth === 0 && (
            <div className="text-[10px] text-zinc-400 font-mono w-6 shrink-0">
              {i + 1}.
            </div>
          )}
          {isEditing ? (
            <>
              <Input value={draft.title}
                onChange={(e) => setEditing((p) => ({
                  ...p, [t.task_id]: { ...p[t.task_id], title: e.target.value } }))}
                placeholder="Task title"
                className="rounded-sm h-8 flex-1"
                data-testid={`proposal-task-edit-title-${t.task_id}`} />
              <select value={draft.priority}
                onChange={(e) => setEditing((p) => ({
                  ...p, [t.task_id]: { ...p[t.task_id], priority: e.target.value } }))}
                className="h-8 text-xs rounded-sm border border-zinc-200 px-2 bg-white"
                data-testid={`proposal-task-edit-priority-${t.task_id}`}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
              <Input type="number" min="0" step="0.5"
                value={draft.estimate_hours}
                onChange={(e) => setEditing((p) => ({
                  ...p, [t.task_id]: { ...p[t.task_id], estimate_hours: e.target.value } }))}
                placeholder="hrs"
                className="rounded-sm h-8 w-16"
                data-testid={`proposal-task-edit-hours-${t.task_id}`} />
              <Button size="sm" onClick={() => saveEdit(t.task_id)}
                className="rounded-sm h-8 bg-[#002FA7] hover:bg-[#001f70] text-white"
                data-testid={`proposal-task-save-${t.task_id}`}>
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => cancelEdit(t.task_id)}
                className="rounded-sm h-8"
                data-testid={`proposal-task-cancel-${t.task_id}`}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-900 truncate">{t.title}</div>
                {t.description && (
                  <div className="text-[11px] text-zinc-500 truncate">{t.description}</div>
                )}
              </div>
              <Badge variant="outline"
                className={`rounded-sm text-[10px] shrink-0 ${priorityColors[t.priority] || priorityColors.normal}`}>
                {t.priority}
              </Badge>
              {t.estimate_hours != null && (
                <div className="text-[11px] text-zinc-500 font-mono w-14 text-right shrink-0">
                  {t.estimate_hours}h
                </div>
              )}
              <Badge variant="outline"
                className={`rounded-sm text-[10px] shrink-0 ${t.status === "pending"
                  ? "bg-zinc-50 text-zinc-500 border-zinc-200"
                  : "bg-sky-50 text-sky-700 border-sky-200"}`}>
                {t.status}
              </Badge>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                onClick={() => toggleSubtask(t.task_id)}
                title="Add subtask"
                data-testid={`proposal-task-subtask-toggle-${t.task_id}`}>
                <Plus className="h-3 w-3 text-zinc-400 hover:text-[#002FA7]" />
              </Button>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                onClick={() => startEdit(t)}
                title="Edit task"
                data-testid={`proposal-task-edit-${t.task_id}`}>
                <Pencil className="h-3 w-3 text-zinc-400 hover:text-[#002FA7]" />
              </Button>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                onClick={() => removeTask(t.task_id)}
                title="Delete task"
                data-testid={`proposal-task-delete-${t.task_id}`}>
                <Trash2 className="h-3 w-3 text-zinc-400 hover:text-red-600" />
              </Button>
            </>
          )}
        </div>
        {sub?.open && (
          <div className="flex items-center gap-2 py-2 border-b border-dashed border-zinc-200 bg-zinc-50/60"
            style={{ paddingLeft: (depth + 1) * 24 }}
            data-testid={`proposal-task-subtask-form-${t.task_id}`}>
            <CornerDownRight className="h-3 w-3 text-zinc-300 shrink-0" />
            <Input value={sub.title}
              onChange={(e) => setSubField(t.task_id, "title", e.target.value)}
              placeholder="Subtask title…"
              className="rounded-sm h-8 flex-1"
              onKeyDown={(e) => {
                if (e.key === "Enter" && sub.title.trim()) {
                  addTask(t.task_id, sub);
                }
              }}
              data-testid={`proposal-task-subtask-title-${t.task_id}`} />
            <select value={sub.priority}
              onChange={(e) => setSubField(t.task_id, "priority", e.target.value)}
              className="h-8 text-xs rounded-sm border border-zinc-200 px-2 bg-white"
              data-testid={`proposal-task-subtask-priority-${t.task_id}`}>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
            <Input type="number" min="0" step="0.5" value={sub.hours}
              onChange={(e) => setSubField(t.task_id, "hours", e.target.value)}
              placeholder="hrs" className="rounded-sm h-8 w-16"
              data-testid={`proposal-task-subtask-hours-${t.task_id}`} />
            <Button size="sm" disabled={adding || !sub.title.trim()}
              onClick={() => addTask(t.task_id, sub)}
              className="rounded-sm h-8 bg-[#002FA7] hover:bg-[#001f70] text-white"
              data-testid={`proposal-task-subtask-add-${t.task_id}`}>
              {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Plus className="h-3.5 w-3.5" />}
              <span className="ml-1">Add</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={() => toggleSubtask(t.task_id)}
              className="rounded-sm h-8"
              data-testid={`proposal-task-subtask-cancel-${t.task_id}`}>
              Cancel
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <Card className="rounded-sm" data-testid="proposal-tasks-section">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-display font-bold text-xl">Tasks</div>
            <p className="text-xs text-zinc-500 mt-1">
              {isAccepted
                ? "Proposal is accepted — new tasks are activated straight onto the project."
                : "Draft tasks stay in `pending` until this proposal is accepted, then they activate onto the project automatically."}
            </p>
          </div>
          <Badge variant="outline"
            className={`rounded-sm ${isAccepted
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-amber-50 text-amber-700 border-amber-200"}`}>
            {isAccepted ? "Live tasks" : "Pending — activates on acceptance"}
          </Badge>
        </div>

        {loading ? (
          <div className="text-sm text-zinc-400 italic py-4">Loading tasks…</div>
        ) : (
          <div className="space-y-0">
            {flatOrdered.length === 0 ? (
              <div className="text-sm text-zinc-400 italic py-3">
                No tasks yet — add the first one below.
              </div>
            ) : flatOrdered.map((item, i) => renderRow(item, i))}
          </div>
        )}

        {/* Add task row */}
        <div className="flex items-center gap-2 pt-2 border-t border-zinc-200">
          <Input value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="New task title…"
            className="rounded-sm h-9 flex-1"
            onKeyDown={(e) => { if (e.key === "Enter") addTask(); }}
            data-testid="proposal-new-task-title" />
          <select value={newPriority}
            onChange={(e) => setNewPriority(e.target.value)}
            className="h-9 text-xs rounded-sm border border-zinc-200 px-2 bg-white"
            data-testid="proposal-new-task-priority">
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
          <Input type="number" min="0" step="0.5"
            value={newHours}
            onChange={(e) => setNewHours(e.target.value)}
            placeholder="hrs"
            className="rounded-sm h-9 w-20"
            data-testid="proposal-new-task-hours" />
          <Button size="sm" onClick={() => addTask()}
            disabled={adding || !newTitle.trim()}
            className="rounded-sm bg-[#002FA7] hover:bg-[#001f70] text-white"
            data-testid="proposal-add-task-btn">
            {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Plus className="h-3.5 w-3.5" />}
            <span className="ml-1">Add task</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


function newGroupKey() {
  return `g_${Math.random().toString(36).slice(2, 9)}`;
}

const RECURRING_LABEL = { one_off: "One-off", monthly: "Monthly", quarterly: "Quarterly", biennial: "Biennial", annual: "Annual" };
const STATUS_COLORS = {
  draft: "bg-zinc-100 text-zinc-700 border-zinc-200",
  internal_review: "bg-amber-100 text-amber-800 border-amber-200",
  sent: "bg-blue-100 text-blue-800 border-blue-200",
  viewed: "bg-indigo-100 text-indigo-800 border-indigo-200",
  accepted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  declined: "bg-red-100 text-red-800 border-red-200",
  expired: "bg-orange-100 text-orange-800 border-orange-200",
  archived: "bg-zinc-200 text-zinc-600 border-zinc-300",
};

const STATUS_OPTIONS = [
  "draft", "internal_review", "sent", "viewed",
  "accepted", "declined", "expired", "archived",
];

export default function ProposalEditorPage({ mode = "proposal" } = {}) {
  const { clientId, proposalId, templateId } = useParams();
  const navigate = useNavigate();
  const isTemplate = mode === "template";
  // For template mode the URL carries templateId; reuse the same state
  // variable name so the rest of the editor doesn't need to branch.
  const entityId = isTemplate ? templateId : proposalId;
  // API endpoint base — singular per-doc actions (GET/PUT/PDF).
  const apiBase = isTemplate
    ? `/proposal-templates/${entityId}`
    : `/proposals/${entityId}`;
  const [proposal, setProposal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agencyDialogOpen, setAgencyDialogOpen] = useState(false);
  const [copyingTemplate, setCopyingTemplate] = useState(false);

  // ── Load proposal/template + (in proposal mode) client contacts + agencies ─
  useEffect(() => {
    (async () => {
      try {
        const normalise = (doc) => ({
          ...doc,
          // Stamp transient `_uid` fields so dnd-kit has stable IDs
          // across renders. Stripped before save.
          domain_options: _stampUids(doc.domain_options, "d"),
          competitors: _stampUids(doc.competitors, "c"),
          // Wrap search terms into {value,_uid} so each row keeps
          // identity even when its text is edited.
          _searchTermRows: _wrapTerms(doc.search_terms),
        });
        if (isTemplate) {
          const { data } = await api.get(apiBase);
          setProposal(normalise(data));
        } else {
          const [{ data }, c, a] = await Promise.all([
            api.get(apiBase),
            api.get(`/clients/${clientId}/contacts`).catch(() => ({ data: [] })),
            api.get(`/agency-profiles`).catch(() => ({ data: { agency_profiles: [] } })),
          ]);
          setProposal(normalise(data));
          setContacts(c.data || []);
          setAgencies(a.data?.agency_profiles || []);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load proposal");
      }
    })();
  }, [apiBase, clientId, isTemplate]);

  // ── Mutators ──────────────────────────────────────────────────────
  const patch = useCallback((updates) => {
    setProposal((p) => ({ ...p, ...updates }));
    setDirty(true);
  }, []);

  const patchSection = useCallback((idx, updates) => {
    setProposal((p) => {
      const next = { ...p, sections: [...p.sections] };
      next.sections[idx] = { ...next.sections[idx], ...updates };
      return next;
    });
    setDirty(true);
  }, []);

  const patchItem = useCallback((sIdx, iIdx, updates) => {
    setProposal((p) => {
      const next = { ...p, sections: [...p.sections] };
      const sec = { ...next.sections[sIdx], items: [...next.sections[sIdx].items] };
      sec.items[iIdx] = { ...sec.items[iIdx], ...updates };
      next.sections[sIdx] = sec;
      return next;
    });
    setDirty(true);
  }, []);

  const addItem = (sIdx) => {
    setProposal((p) => {
      const next = { ...p, sections: [...p.sections] };
      const sec = { ...next.sections[sIdx], items: [...next.sections[sIdx].items] };
      sec.items.push({
        id: `it_${Math.random().toString(36).slice(2, 10)}`,
        title: "New line item", description: "",
        qty: 1, unit: "each", unit_price: 0, recurring: "one_off",
        included: true, notes: "",
      });
      next.sections[sIdx] = sec;
      return next;
    });
    setDirty(true);
  };

  const removeItem = (sIdx, iIdx) => {
    setProposal((p) => {
      const next = { ...p, sections: [...p.sections] };
      const sec = { ...next.sections[sIdx], items: [...next.sections[sIdx].items] };
      sec.items.splice(iIdx, 1);
      next.sections[sIdx] = sec;
      return next;
    });
    setDirty(true);
  };

  const addSection = () => {
    setProposal((p) => ({
      ...p,
      sections: [...p.sections, {
        key: `custom_${Date.now()}`,
        title: "New section", description: "", items: [],
      }],
    }));
    setDirty(true);
  };

  const removeSection = (sIdx) => {
    if (!window.confirm("Remove this entire section?")) return;
    setProposal((p) => {
      const next = { ...p, sections: [...p.sections] };
      next.sections.splice(sIdx, 1);
      return next;
    });
    setDirty(true);
  };

  // ── Drag-and-drop reorder handlers ────────────────────────────────
  // Each handler computes the new array, updates state for instant UI
  // feedback, AND triggers an autosave so the new order persists.
  const reorderSections = (fromIdx, toIdx) => {
    setProposal((p) => {
      const arr = [...(p.sections || [])];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      const next = { ...p, sections: arr };
      autosave(next);
      return next;
    });
    setDirty(true);
  };

  const reorderItems = (sIdx, fromIdx, toIdx) => {
    setProposal((p) => {
      const sections = [...(p.sections || [])];
      const sec = { ...sections[sIdx], items: [...sections[sIdx].items] };
      const [moved] = sec.items.splice(fromIdx, 1);
      sec.items.splice(toIdx, 0, moved);
      sections[sIdx] = sec;
      const next = { ...p, sections };
      autosave(next);
      return next;
    });
    setDirty(true);
  };

  const reorderDomains = (fromIdx, toIdx) => {
    setProposal((p) => {
      const arr = [...(p.domain_options || [])];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      const next = { ...p, domain_options: arr };
      autosave(next);
      return next;
    });
    setDirty(true);
  };

  const reorderCompetitors = (fromIdx, toIdx) => {
    setProposal((p) => {
      const arr = [...(p.competitors || [])];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      const next = { ...p, competitors: arr };
      autosave(next);
      return next;
    });
    setDirty(true);
  };

  const reorderSearchTerms = (fromIdx, toIdx) => {
    setProposal((p) => {
      const arr = [...(p._searchTermRows || [])];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      const next = { ...p, _searchTermRows: arr };
      autosave(next);
      return next;
    });
    setDirty(true);
  };

  // Domain manipulations
  const addDomain = () => {
    setProposal((p) => ({
      ...p, domain_options: [...(p.domain_options || []), {
        _uid: _genUid("d"),
        domain: "", availability: "unknown", note: "", preferred: false,
      }],
    }));
    setDirty(true);
  };
  const patchDomain = (i, updates) => {
    setProposal((p) => {
      const list = [...(p.domain_options || [])];
      list[i] = { ...list[i], ...updates };
      return { ...p, domain_options: list };
    });
    setDirty(true);
  };
  const removeDomain = (i) => {
    setProposal((p) => {
      const list = [...(p.domain_options || [])];
      list.splice(i, 1);
      return { ...p, domain_options: list };
    });
    setDirty(true);
  };
  const checkDomain = async (i) => {
    const d = proposal.domain_options[i];
    if (!d.domain) { toast.error("Enter a domain first"); return; }
    patchDomain(i, { availability: "unknown", note: "checking…" });
    try {
      const { data } = await api.post("/proposals/check-domain", { domain: d.domain });
      patchDomain(i, {
        availability: data.availability, note: data.note || "",
        checked_at: new Date().toISOString(),
      });
    } catch (e) {
      patchDomain(i, { availability: "error", note: "Lookup failed" });
    }
  };

  // ── Save / Send / Status ──────────────────────────────────────────
  // Build the payload that goes back to the backend. Strips transient
  // `_uid` markers we added for dnd-kit and serialises the search-term
  // rows back to a plain list of strings.
  const _toPayload = useCallback((p) => {
    if (!p) return null;
    const out = { ...p,
      domain_options: _stripUids(p.domain_options),
      competitors: _stripUids(p.competitors),
      search_terms: _unwrapTerms(p._searchTermRows || p.search_terms),
    };
    delete out._searchTermRows;
    return out;
  }, []);

  const save = async () => {
    if (!proposal) return;
    setSaving(true);
    try {
      const { data } = await api.put(apiBase, _toPayload(proposal));
      // Re-stamp uids on the server's response so the editor state
      // stays in sync with the next save cycle.
      setProposal({
        ...data,
        domain_options: _stampUids(data.domain_options, "d"),
        competitors: _stampUids(data.competitors, "c"),
        _searchTermRows: _wrapTerms(data.search_terms),
      });
      setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Autosave used by drag-and-drop reorder handlers. Sends a specific
  // next-state to the backend without waiting for setProposal to flush
  // (avoids stale closures). Doesn't toast on success — the UI animation
  // is the feedback.
  const autosave = useCallback(async (nextProposal) => {
    if (!nextProposal) return;
    try {
      await api.put(apiBase, _toPayload(nextProposal));
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-save failed");
    }
  }, [apiBase, _toPayload]);

  const setStatus = async (status) => {
    if (isTemplate) return;
    try {
      await api.post(`/proposals/${entityId}/transition`, { status });
      setProposal((p) => ({ ...p, status }));
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transition failed");
    }
  };

  // "COPY TO TEMPLATES" — clones the current proposal into a new
  // template so the admin can reuse its structure on future quotes.
  const copyToTemplates = async () => {
    if (!window.confirm(
      "Save this proposal's structure as a reusable template? You can " +
      "find it under the Proposal Templates menu.")) return;
    setCopyingTemplate(true);
    try {
      const { data } = await api.post(
        `/proposal-templates/from-proposal/${entityId}`,
      );
      toast.success(`Copied to template: ${data.title}`);
      if (window.confirm("Template created. Open it now?")) {
        navigate(`/templates/${data.template_id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Copy failed");
    } finally {
      setCopyingTemplate(false);
    }
  };

  // ── Totals ─────────────────────────────────────────────────────────
  // Live calculator — mirrors backend `compute_totals()` so the strip
  // updates instantly as the user edits qty / unit_price / cadence /
  // included. Without this it'd show stale numbers from the last save
  // and feel "broken" while editing.
  const totals = useMemo(() => {
    if (!proposal) return {};
    const gst = Number(proposal.gst_rate || 0);
    let one_off = 0, monthly = 0, quarterly = 0, biennial = 0, annual = 0;

    // Pick the items that actually count for a section, respecting
    // requires_selection (pick-one radio) and choice_group semantics —
    // mirrors backend `_effective_items` in proposal_models.py. Without
    // this, an admin who leaves multiple radio tiers `included=True`
    // sees their totals silently double-count vs the URL/PDF.
    const effectiveItems = (sec) => {
      const items = (sec.items || []).filter((it) => !it.hidden);
      const counts = (it) => (it.included || it.recommended) && Number(it.qty || 0) > 0;
      if (sec.requires_selection) {
        const cands = items.filter(counts);
        if (!cands.length) return [];
        return [cands.find((x) => x.recommended) || cands[0]];
      }
      const standalone = [];
      const groupWinner = {};
      items.forEach((it) => {
        if (!counts(it)) return;
        if (it.choice_group) {
          const cur = groupWinner[it.choice_group];
          if (!cur || (it.recommended && !cur.recommended)) {
            groupWinner[it.choice_group] = it;
          }
        } else {
          standalone.push(it);
        }
      });
      return [...standalone, ...Object.values(groupWinner)];
    };

    (proposal.sections || []).forEach((sec) => {
      effectiveItems(sec).forEach((it) => {
        const line = Number(it.qty) * Number(it.unit_price);
        if (it.recurring === "monthly") monthly += line;
        else if (it.recurring === "quarterly") quarterly += line;
        else if (it.recurring === "biennial") biennial += line;
        else if (it.recurring === "annual") annual += line;
        else one_off += line;
      });
    });
    const round2 = (n) => Math.round(n * 100) / 100;
    const year_one_sub = one_off + monthly * 12 + quarterly * 4 + biennial + annual;
    return {
      one_off_subtotal: round2(one_off),
      one_off_gst: round2(one_off * gst),
      one_off_total: round2(one_off * (1 + gst)),
      monthly_subtotal: round2(monthly),
      monthly_gst: round2(monthly * gst),
      monthly_total: round2(monthly * (1 + gst)),
      quarterly_subtotal: round2(quarterly),
      quarterly_gst: round2(quarterly * gst),
      quarterly_total: round2(quarterly * (1 + gst)),
      biennial_subtotal: round2(biennial),
      biennial_gst: round2(biennial * gst),
      biennial_total: round2(biennial * (1 + gst)),
      annual_subtotal: round2(annual),
      annual_gst: round2(annual * gst),
      annual_total: round2(annual * (1 + gst)),
      year_one_subtotal: round2(year_one_sub),
      year_one_gst: round2(year_one_sub * gst),
      year_one_total: round2(year_one_sub * (1 + gst)),
      gst_rate: gst,
      currency: proposal.currency,
    };
  }, [proposal]);

  if (!proposal) {
    return (
      <div className="p-8 flex items-center gap-2 text-zinc-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl" data-testid={isTemplate ? "template-editor-page" : "proposal-editor-page"}>
      {/* Header strip */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to={isTemplate ? "/templates" : `/clients/${clientId}/proposals`}
            className="inline-flex items-center text-xs text-zinc-500 hover:text-zinc-800">
            <ArrowLeft className="h-3.5 w-3.5 mr-1" />
            {isTemplate ? "Back to templates" : "Back to proposals"}
          </Link>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">
            {proposal.title}
          </h1>
          {isTemplate && (
            <Badge className="rounded-sm font-mono text-[10px] bg-purple-100 text-purple-800 border-purple-200 mt-1"
              data-testid="template-badge">
              TEMPLATE
            </Badge>
          )}
          <div className="text-[11px] font-mono text-zinc-500 mt-1"
            data-testid="proposal-timestamps">
            Created{" "}
            <span data-testid="proposal-created-at">
              {proposal.created_at
                ? new Date(proposal.created_at).toLocaleString("en-AU", {
                    day: "2-digit", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })
                : "—"}
            </span>
            {proposal.updated_at && proposal.updated_at !== proposal.created_at && (
              <>
                {" · Updated "}
                <span data-testid="proposal-updated-at">
                  {new Date(proposal.updated_at).toLocaleString("en-AU", {
                    day: "2-digit", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </span>
              </>
            )}
            {!isTemplate && proposal.accepted_at && (
              <>
                {" · Accepted "}
                <span data-testid="proposal-accepted-at">
                  {new Date(proposal.accepted_at).toLocaleString("en-AU", {
                    day: "2-digit", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </span>
              </>
            )}
          </div>
          {!isTemplate && (
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge className={`rounded-sm font-mono text-[10px] ${STATUS_COLORS[proposal.status] || STATUS_COLORS.draft}`}
                data-testid="proposal-status-badge">
                {proposal.status}
              </Badge>
              <select value={proposal.status}
                onChange={(e) => setStatus(e.target.value)}
                className="h-7 text-[11px] font-mono rounded-sm border border-zinc-200 bg-white px-1.5"
                data-testid="proposal-status-select">
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {proposal.share_token && (
                <a target="_blank" rel="noreferrer"
                  href={`/proposals/share/${proposal.share_token}`}
                  className="text-[11px] underline text-zinc-600"
                  data-testid="proposal-public-share-link">
                  <ExternalLink className="h-3 w-3 inline" /> Public share link
                </a>
              )}
              <a target="_blank" rel="noreferrer"
                href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/proposals/${entityId}/pdf`}
                className="text-[11px] underline text-zinc-600"
                data-testid="proposal-pdf-link">
                <FileText className="h-3 w-3 inline" /> PDF preview
              </a>
            </div>
          )}
          {isTemplate && (
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <a target="_blank" rel="noreferrer"
                href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/proposal-templates/${entityId}/pdf`}
                className="text-[11px] underline text-zinc-600"
                data-testid="template-pdf-link">
                <FileText className="h-3 w-3 inline" /> PDF preview
              </a>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={save} disabled={saving || !dirty}
            variant="outline" className="rounded-sm"
            data-testid="proposal-save-btn">
            {saving
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Save className="h-4 w-4 mr-2" />}
            {dirty ? "Save changes" : "Saved"}
          </Button>
          {!isTemplate && (
            <>
              <Button onClick={copyToTemplates} disabled={copyingTemplate}
                variant="outline" className="rounded-sm"
                data-testid="proposal-copy-to-templates-btn"
                title="Save this proposal's structure as a reusable template">
                {copyingTemplate
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Copy className="h-4 w-4 mr-2" />}
                Copy to templates
              </Button>
              <Button onClick={() => setSendOpen(true)}
                className="rounded-sm bg-[#002FA7] hover:bg-[#001f70] text-white"
                data-testid="proposal-send-btn">
                <Send className="h-4 w-4 mr-2" /> Send to client
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Document header card */}
      <Card className="rounded-sm">
        <CardContent className="p-6 grid md:grid-cols-2 gap-3">
          {/* Sender agency picker — supports multi-brand sales (e.g.
              Iceberg Digital + Image Traders). Hidden in template mode
              because templates aren't pinned to a sender agency — the
              correct agency is snapshotted when the template is
              instantiated against a real client. */}
          {!isTemplate && (
            <Field label="From (sender agency)" full>
              <div className="flex gap-2 items-center flex-wrap">
                <select value={proposal.from_agency_id || ""}
                  onChange={(e) => {
                    const id = e.target.value;
                    if (!id) {
                      patch({
                        from_agency_id: null, from_logo_url: "",
                        from_address: "", from_abn: "", from_email: "",
                        from_phone: "", from_website: "",
                      });
                      return;
                    }
                    const a = agencies.find((x) => x.agency_profile_id === id);
                    if (!a) return;
                    patch({
                      from_agency_id: a.agency_profile_id,
                      prepared_by: a.name,
                      from_logo_url: a.logo_url || "",
                      from_address: a.address || "",
                      from_abn: a.abn || "",
                      from_email: a.email || "",
                      from_phone: a.phone || "",
                      from_website: a.website || "",
                    });
                  }}
                  className="h-10 rounded-sm border border-zinc-200 px-2 text-sm flex-1"
                  data-testid="proposal-from-agency-select">
                  <option value="">— None (use prepared_by below) —</option>
                  {agencies.map((a) => (
                    <option key={a.agency_profile_id} value={a.agency_profile_id}>
                      {a.name}{a.is_default ? " · default" : ""}
                    </option>
                  ))}
                </select>
                <Button size="sm" variant="outline" className="rounded-sm"
                  onClick={() => setAgencyDialogOpen(true)}
                  data-testid="proposal-manage-agencies-btn">
                  Manage senders
                </Button>
              </div>
              {proposal.from_logo_url && (
                <img src={proposal.from_logo_url} alt="" className="mt-2 h-12 object-contain border border-zinc-100 rounded-sm bg-white p-1" />
              )}
            </Field>
          )}
          <Field label={isTemplate ? "Template name" : "Title"} testid="proposal-title-input" full={isTemplate}>
            <Input value={proposal.title || ""}
              onChange={(e) => patch({ title: e.target.value })}
              data-testid="proposal-title-input" />
          </Field>
          {!isTemplate && (
            <>
              <Field label="Prepared for" testid="proposal-prepared-for-input">
                <Input value={proposal.prepared_for || ""}
                  onChange={(e) => patch({ prepared_for: e.target.value })}
                  data-testid="proposal-prepared-for-input" />
              </Field>
              <Field label="Prepared by (agency display name)">
                <Input value={proposal.prepared_by || ""}
                  onChange={(e) => patch({ prepared_by: e.target.value })} />
              </Field>
              <Field label="Valid until (review required after this date)">
                <Input type="date" value={(proposal.valid_until || "").slice(0, 10)}
                  onChange={(e) => patch({ valid_until: e.target.value })}
                  data-testid="proposal-valid-until-input" />
              </Field>
            </>
          )}
          <Field label="Currency">
            <select value={proposal.currency || "AUD"}
              onChange={(e) => patch({ currency: e.target.value })}
              className="w-full h-10 rounded-sm border border-zinc-200 px-2 text-sm">
              {["AUD", "USD", "GBP", "EUR", "NZD"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label={`GST rate (${((proposal.gst_rate || 0) * 100).toFixed(0)}%)`}>
            <Input type="number" step="0.01" value={proposal.gst_rate || 0}
              onChange={(e) => patch({ gst_rate: Number(e.target.value) })} />
          </Field>
          <Field label="Project objectives (client brief)" full>
            <Textarea rows={6} value={proposal.objectives || ""}
              onChange={(e) => patch({ objectives: e.target.value })} />
          </Field>
          <Field label="Proposal overview (intro)" full>
            <Textarea rows={6} value={proposal.introduction || ""}
              onChange={(e) => patch({ introduction: e.target.value })} />
          </Field>
        </CardContent>
      </Card>

      {/* Domain options — client-specific, hidden in template mode. */}
      {!isTemplate && (
      <Card className="rounded-sm" data-testid="proposal-domains-card">
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-bold text-xl">Domain name options</div>
              <p className="text-[12px] text-zinc-500">Add candidate domains and check availability so the client can pick.</p>
            </div>
            <Button size="sm" variant="outline" className="rounded-sm" onClick={addDomain}
              data-testid="proposal-add-domain">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add domain
            </Button>
          </div>
          <SortableList
            ids={(proposal.domain_options || []).map((d, i) => d._uid || `d_${i}`)}
            onReorder={reorderDomains}>
          <div className="space-y-3">
            {(proposal.domain_options || []).map((d, i) => (
              <SortableItem key={d._uid || `d_${i}`} id={d._uid || `d_${i}`}>
                {({ handleProps, isDragging }) => (
              <div className={`border border-zinc-200 rounded-sm p-3 space-y-2 ${
                  isDragging ? "ring-2 ring-zinc-300 shadow bg-white" : ""}`}
                data-testid={`proposal-domain-row-${i}`}>
                {/* Row 1 — drag handle + star + domain + availability + actions */}
                <div className="grid grid-cols-12 gap-2 items-center">
                  <button type="button" {...handleProps}
                    className="col-span-1 cursor-grab active:cursor-grabbing touch-none h-9 w-9 grid place-items-center rounded-sm text-zinc-400 hover:text-zinc-700"
                    title="Drag to reorder domain"
                    aria-label="Drag to reorder domain"
                    data-testid={`proposal-domain-${i}-drag-handle`}>
                    <GripVertical className="h-4 w-4" />
                  </button>
                  <button type="button"
                    onClick={() => patchDomain(i, { preferred: !d.preferred })}
                    className={`col-span-1 h-9 w-9 grid place-items-center rounded-sm border ${
                      d.preferred ? "bg-amber-100 border-amber-300 text-amber-700"
                                  : "bg-white border-zinc-200 text-zinc-400"}`}
                    title={d.preferred ? "Preferred — clear" : "Mark as preferred"}>
                    <Star className={`h-4 w-4 ${d.preferred ? "fill-amber-500" : ""}`} />
                  </button>
                  <div className="col-span-6">
                    <Input value={d.domain} placeholder="example.com.au"
                      onChange={(e) => patchDomain(i, { domain: e.target.value })}
                      data-testid={`proposal-domain-input-${i}`} />
                  </div>
                  <div className="col-span-3">
                    <select value={d.availability || "unknown"}
                      onChange={(e) => patchDomain(i, { availability: e.target.value })}
                      data-testid={`proposal-domain-avail-${i}`}
                      className={`h-9 w-full text-[11px] font-mono rounded-sm border px-1.5 ${
                        d.availability === "available" || d.availability === "manual_available"
                          ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
                        d.availability === "taken" ? "bg-red-50 border-red-200 text-red-800" :
                        d.availability === "premium" ? "bg-amber-50 border-amber-200 text-amber-800" :
                        d.availability === "error" ? "bg-zinc-100 border-zinc-300 text-zinc-700" :
                        "bg-white border-zinc-200 text-zinc-700"
                      }`}
                      title="Override the RDAP lookup result if you know the domain is owned by the client (manual available)">
                      <option value="unknown">Unknown</option>
                      <option value="available">Available (RDAP)</option>
                      <option value="manual_available">Available (manual override)</option>
                      <option value="taken">Taken</option>
                      <option value="premium">Premium / in-transition</option>
                      <option value="error">Error</option>
                    </select>
                  </div>
                  <div className="col-span-1 flex items-center justify-end gap-1">
                    <Button size="sm" variant="ghost" className="rounded-sm h-7 px-1"
                      onClick={() => checkDomain(i)}
                      data-testid={`proposal-check-domain-${i}`}
                      title="Check availability via RDAP">
                      <Search className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-sm h-7 px-1 text-red-600"
                      onClick={() => removeDomain(i)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Row 2 — renewal cycle + price + free-text note */}
                <div className="grid grid-cols-12 gap-2 items-center">
                  <Label className="col-span-2 label-overline text-[10px] text-right pr-1">
                    Renewal
                  </Label>
                  <div className="col-span-2">
                    <select value={d.renewal_years || 1}
                      onChange={(e) => patchDomain(i, { renewal_years: Number(e.target.value) })}
                      data-testid={`proposal-domain-years-${i}`}
                      className="h-8 w-full text-xs rounded-sm border border-zinc-200 px-1">
                      {[1, 2, 3, 5, 10].map((y) => (
                        <option key={y} value={y}>{y} {y === 1 ? "year" : "years"}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-3">
                    <Input type="number" step="0.01" min="0"
                      value={d.renewal_price || 0}
                      onChange={(e) => patchDomain(i, { renewal_price: Number(e.target.value) })}
                      placeholder="Renewal price"
                      data-testid={`proposal-domain-price-${i}`}
                      className="h-8 text-xs text-right" />
                  </div>
                  <div className="col-span-5">
                    <Input value={d.note || ""} placeholder="Notes (registrar, lock status, …)"
                      onChange={(e) => patchDomain(i, { note: e.target.value })}
                      className="h-8 text-xs" />
                  </div>
                </div>

                {/* Row 3 — required + recommended toggles. Required
                    pre-ticks AND locks; Recommended pre-ticks but lets
                    the client un-tick. */}
                <div className="grid grid-cols-12 gap-2 items-center">
                  <Label className="col-span-2 label-overline text-[10px] text-right pr-1">
                    Required
                  </Label>
                  <label className="col-span-10 inline-flex items-center gap-1.5 text-[11px] text-zinc-600 cursor-pointer">
                    <input type="checkbox" checked={!!d.required}
                      onChange={(e) => patchDomain(i, { required: e.target.checked })}
                      data-testid={`proposal-domain-required-${i}`} />
                    <Lock className="h-3 w-3" />
                    Lock as compulsory on the client view (client can&apos;t un-tick)
                  </label>
                </div>
                <div className="grid grid-cols-12 gap-2 items-center">
                  <Label className="col-span-2 label-overline text-[10px] text-right pr-1">
                    Recommended
                  </Label>
                  <label className="col-span-10 inline-flex items-center gap-1.5 text-[11px] text-zinc-600 cursor-pointer">
                    <input type="checkbox" checked={!!d.recommended}
                      onChange={(e) => patchDomain(i, { recommended: e.target.checked })}
                      data-testid={`proposal-domain-recommended-${i}`} />
                    Pre-tick on the client view (client can still un-tick)
                  </label>
                </div>
              </div>
                )}
              </SortableItem>
            ))}
            {(proposal.domain_options || []).length === 0 && (
              <div className="text-[12px] text-zinc-500 italic">No domains yet — click <strong>Add domain</strong>.</div>
            )}
          </div>
          </SortableList>
        </CardContent>
      </Card>
      )}

      {/* Sections */}
      <SortableList
        ids={(proposal.sections || []).map((s, i) => s.key || `sec_${i}`)}
        onReorder={reorderSections}>
        {(proposal.sections || []).map((sec, sIdx) => (
          <SortableItem key={sec.key || sIdx} id={sec.key || `sec_${sIdx}`}>
            {({ handleProps, isDragging }) => (
              <SectionEditor
                section={sec}
                currency={proposal.currency || "AUD"}
                onPatch={(u) => patchSection(sIdx, u)}
                onPatchItem={(iIdx, u) => patchItem(sIdx, iIdx, u)}
                onAddItem={() => addItem(sIdx)}
                onRemoveItem={(iIdx) => removeItem(sIdx, iIdx)}
                onRemove={() => removeSection(sIdx)}
                onReorderItem={(from, to) => reorderItems(sIdx, from, to)}
                testidPrefix={`proposal-section-${sec.key}`}
                dragHandleProps={handleProps}
                isDragging={isDragging}
              />
            )}
          </SortableItem>
        ))}
      </SortableList>

      <div>
        <Button variant="outline" size="sm" className="rounded-sm"
          onClick={addSection} data-testid="proposal-add-section">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add custom section
        </Button>
      </div>

      {/* Competitors + Search terms — SEO seed inputs */}
      <Card className="rounded-sm" data-testid="proposal-competitors-card">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-display font-bold text-lg">Competitor or industry sample websites</h3>
              <p className="text-[11px] text-zinc-500">
                Up to 10. Seeded into our rank-tracking + backlink tools the
                moment the client accepts.
              </p>
            </div>
            <Button size="sm" variant="outline" className="rounded-sm h-7"
              disabled={(proposal.competitors || []).length >= 10}
              onClick={() => patch({
                competitors: [...(proposal.competitors || []),
                              { _uid: _genUid("c"), name: "", url: "" }],
              })}
              data-testid="proposal-add-competitor">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add competitor
            </Button>
          </div>
          {(proposal.competitors || []).length === 0 ? (
            <p className="text-[11px] text-zinc-500 italic">No competitors added.</p>
          ) : (
            <SortableList
              ids={(proposal.competitors || []).map((c, i) => c._uid || `c_${i}`)}
              onReorder={reorderCompetitors}>
            <div className="space-y-1.5">
              {(proposal.competitors || []).map((c, ci) => (
                <SortableItem key={c._uid || `c_${ci}`} id={c._uid || `c_${ci}`}>
                  {({ handleProps, isDragging }) => (
                <div className={`grid grid-cols-12 gap-2 items-center ${
                    isDragging ? "ring-2 ring-zinc-300 rounded-sm bg-white" : ""}`}
                  data-testid={`proposal-competitor-${ci}`}>
                  <button type="button" {...handleProps}
                    className="col-span-1 cursor-grab active:cursor-grabbing touch-none h-8 w-8 grid place-items-center text-zinc-400 hover:text-zinc-700 rounded-sm"
                    title="Drag to reorder competitor"
                    aria-label="Drag to reorder competitor"
                    data-testid={`proposal-competitor-${ci}-drag-handle`}>
                    <GripVertical className="h-3.5 w-3.5" />
                  </button>
                  <Input value={c.name || ""} placeholder="Brand name"
                    onChange={(e) => {
                      const next = [...(proposal.competitors || [])];
                      next[ci] = { ...next[ci], name: e.target.value };
                      patch({ competitors: next });
                    }}
                    className="col-span-4 h-8 text-xs"
                    data-testid={`proposal-competitor-${ci}-name`} />
                  <Input type="url" value={c.url || ""} placeholder="https://competitor.com.au"
                    onChange={(e) => {
                      const next = [...(proposal.competitors || [])];
                      next[ci] = { ...next[ci], url: e.target.value };
                      patch({ competitors: next });
                    }}
                    className="col-span-6 h-8 text-xs"
                    data-testid={`proposal-competitor-${ci}-url`} />
                  <Button size="sm" variant="ghost"
                    className="col-span-1 h-8 px-1 text-red-600"
                    onClick={() => {
                      const next = (proposal.competitors || []).filter((_, k) => k !== ci);
                      patch({ competitors: next });
                    }}
                    data-testid={`proposal-competitor-${ci}-remove`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                  )}
                </SortableItem>
              ))}
            </div>
            </SortableList>
          )}

          <div className="border-t border-zinc-200 pt-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-display font-bold text-lg">Priority search words &amp; phrases</h3>
                <p className="text-[11px] text-zinc-500">
                  Up to 50. Used to focus on-page SEO and our monthly
                  rank-tracking report. Drag the handles to reorder
                  priority.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-zinc-500"
                  data-testid="proposal-search-terms-count">
                  {(proposal._searchTermRows || []).length} / 50
                </span>
                <Button size="sm" variant="outline" className="rounded-sm h-7"
                  disabled={(proposal._searchTermRows || []).length >= 50}
                  onClick={() => patch({
                    _searchTermRows: [
                      ...(proposal._searchTermRows || []),
                      { _uid: _genUid("t"), value: "" },
                    ],
                  })}
                  data-testid="proposal-add-search-term">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add term
                </Button>
              </div>
            </div>
            {(proposal._searchTermRows || []).length === 0 ? (
              <p className="text-[11px] text-zinc-500 italic mt-2">No search terms added.</p>
            ) : (
              <SortableList
                ids={(proposal._searchTermRows || []).map((t) => t._uid)}
                onReorder={reorderSearchTerms}>
              <div className="space-y-1.5 mt-3">
                {(proposal._searchTermRows || []).map((t, ti) => (
                  <SortableItem key={t._uid} id={t._uid}>
                    {({ handleProps, isDragging }) => (
                    <div className={`grid grid-cols-12 gap-2 items-center ${
                        isDragging ? "ring-2 ring-zinc-300 rounded-sm bg-white" : ""}`}
                      data-testid={`proposal-search-term-${ti}`}>
                      <button type="button" {...handleProps}
                        className="col-span-1 cursor-grab active:cursor-grabbing touch-none h-8 w-8 grid place-items-center text-zinc-400 hover:text-zinc-700 rounded-sm"
                        title="Drag to reorder search term"
                        aria-label="Drag to reorder search term"
                        data-testid={`proposal-search-term-${ti}-drag-handle`}>
                        <GripVertical className="h-3.5 w-3.5" />
                      </button>
                      <Input value={t.value || ""}
                        placeholder="e.g. cosmetic dentist sydney"
                        onChange={(e) => {
                          const next = [...(proposal._searchTermRows || [])];
                          next[ti] = { ...next[ti], value: e.target.value };
                          patch({ _searchTermRows: next });
                        }}
                        className="col-span-10 h-8 text-xs font-mono"
                        data-testid={`proposal-search-term-${ti}-input`} />
                      <Button size="sm" variant="ghost"
                        className="col-span-1 h-8 px-1 text-red-600"
                        onClick={() => {
                          const next = (proposal._searchTermRows || [])
                            .filter((_, k) => k !== ti);
                          patch({ _searchTermRows: next });
                        }}
                        data-testid={`proposal-search-term-${ti}-remove`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    )}
                  </SortableItem>
                ))}
              </div>
              </SortableList>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Long-form footer fields */}
      <Card className="rounded-sm">
        <CardContent className="p-6 grid md:grid-cols-2 gap-3">
          <Field label="Inclusions & exclusions" full>
            <Textarea rows={10} className="min-h-[220px]"
              value={proposal.inclusions_exclusions || ""}
              onChange={(e) => patch({ inclusions_exclusions: e.target.value })} />
          </Field>
          <Field label="Client Expectations and Deliverables" full>
            <Textarea rows={10} className="min-h-[220px]"
              value={proposal.client_expectations || ""}
              onChange={(e) => patch({ client_expectations: e.target.value })}
              placeholder="What we'll need from the client, what they'll receive at each milestone, review windows, approval expectations, etc."
              data-testid="proposal-client-expectations" />
          </Field>
          <Field label="Payment terms" full>
            <Textarea rows={10} className="min-h-[220px]"
              value={proposal.payment_terms || ""}
              onChange={(e) => patch({ payment_terms: e.target.value })} />
          </Field>
          <Field label="Compliance & approval (AHPRA, privacy, clinical review etc.)" full>
            <Textarea rows={10} className="min-h-[220px]"
              value={proposal.compliance_notes || ""}
              onChange={(e) => patch({ compliance_notes: e.target.value })} />
          </Field>
          <Field label="Expected outcome" full>
            <Textarea rows={10} className="min-h-[220px]"
              value={proposal.expected_outcome || ""}
              onChange={(e) => patch({ expected_outcome: e.target.value })} />
          </Field>
        </CardContent>
      </Card>

      {/* Totals strip — Big number = ex-GST. Fine print = inc GST. */}
      <Card className="rounded-sm bg-[#0B0B0B] text-white">
        <CardContent className="p-6 grid md:grid-cols-6 gap-4">
          <Total label="One-off" v={totals.one_off_subtotal} cur={proposal.currency} sub={totals.one_off_total} />
          <Total label="Monthly" v={totals.monthly_subtotal} cur={proposal.currency} sub={totals.monthly_total} />
          <Total label="Quarterly" v={totals.quarterly_subtotal} cur={proposal.currency} sub={totals.quarterly_total} />
          <Total label="Annual" v={totals.annual_subtotal} cur={proposal.currency} sub={totals.annual_total} />
          <Total label="Biennial" v={totals.biennial_subtotal} cur={proposal.currency} sub={totals.biennial_total} />
          <Total label="First-year total" v={totals.year_one_subtotal} cur={proposal.currency}
            sub={totals.year_one_total} highlight />
        </CardContent>
      </Card>

      {/* Tasks — pending until the proposal is accepted, then flip live onto the project. */}
      {!isTemplate && entityId && (
        <ProposalTasksSection proposalId={entityId} proposalStatus={proposal.status} />
      )}

      {!isTemplate && (
        <>
          <SendDialog open={sendOpen} onOpenChange={setSendOpen}
            contacts={contacts} proposal={proposal} proposalId={entityId}
            onSent={(res) => {
              setProposal((p) => ({ ...p, status: "sent", share_token: res.share_token,
                sent_at: new Date().toISOString() }));
              toast.success(`Sent to ${res.sent_to.length} recipient${res.sent_to.length === 1 ? "" : "s"}`);
            }} />

          <AgencyManagerDialog open={agencyDialogOpen} onOpenChange={setAgencyDialogOpen}
            agencies={agencies} onChange={setAgencies} />
        </>
      )}

      {/* Floating save button — fixed bottom-right of the viewport so
          the user can save from anywhere on this very long editor
          without scrolling back to the header. Only renders when
          there are unsaved changes (or a save is in flight) to keep
          the UI uncluttered when the page is already saved. */}
      {(dirty || saving) && (
        <div className="fixed bottom-6 right-6 z-50"
          data-testid="proposal-floating-save-wrap">
          <Button onClick={save} disabled={saving || !dirty}
            className="rounded-sm bg-[#002FA7] hover:bg-[#001f70] text-white shadow-2xl ring-1 ring-zinc-900/10 h-11 px-5"
            data-testid="proposal-floating-save-btn">
            {saving
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Save className="h-4 w-4 mr-2" />}
            <span className="font-semibold">
              {saving ? "Saving…" : "Save changes"}
            </span>
          </Button>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Section editor + helpers
// ──────────────────────────────────────────────────────────────────────────

function SectionEditor({ section, currency, onPatch, onPatchItem,
                        onAddItem, onRemoveItem, onRemove, onReorderItem,
                        testidPrefix, dragHandleProps, isDragging }) {
  // Drag-to-group state — id of the item currently being dragged so the
  // drop targets can highlight + so onDrop can resolve the merge.
  const [dragId, setDragId] = React.useState(null);

  const findIdx = (id) => section.items.findIndex((x) => x.id === id);

  const mergeIntoGroup = (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    const srcIdx = findIdx(sourceId);
    const tgtIdx = findIdx(targetId);
    if (srcIdx < 0 || tgtIdx < 0) return;
    const target = section.items[tgtIdx];
    const source = section.items[srcIdx];
    let groupKey = target.choice_group || source.choice_group;
    if (!groupKey) groupKey = newGroupKey();
    // Both join the target's group (or a new one if neither was grouped).
    onPatchItem(srcIdx, { choice_group: groupKey });
    if (!target.choice_group) onPatchItem(tgtIdx, { choice_group: groupKey });
  };

  const clearGroup = (iIdx) => onPatchItem(iIdx, { choice_group: "" });

  return (
    <Card className={`rounded-sm ${isDragging ? "ring-2 ring-zinc-300 shadow-lg" : ""}`}
      data-testid={testidPrefix}>
      <CardContent className="p-6 space-y-3">
        <div className="flex items-center justify-between gap-3">
          {/* Section drag handle — initiates a section-level reorder. */}
          {dragHandleProps && (
            <button type="button" {...dragHandleProps}
              className="cursor-grab active:cursor-grabbing touch-none p-1 -ml-1 text-zinc-400 hover:text-zinc-700 rounded-sm"
              title="Drag to reorder section"
              aria-label="Drag to reorder section"
              data-testid={`${testidPrefix}-drag-handle`}>
              <GripVertical className="h-4 w-4" />
            </button>
          )}
          <div className="flex-1 min-w-0">
            <Input value={section.title}
              onChange={(e) => onPatch({ title: e.target.value })}
              className="font-display font-bold text-lg border-0 px-0 focus-visible:ring-0"
              data-testid={`${testidPrefix}-title-input`} />
          </div>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wide text-zinc-500"
              title="When set, the client must pick exactly one item in this section before they can accept.">
              <span>Behaviour</span>
              <select
                value={section.requires_selection ? "pick_one" : "pick_any"}
                onChange={(e) => onPatch({
                  requires_selection: e.target.value === "pick_one",
                })}
                className="h-7 rounded-sm border border-zinc-200 text-[11px] px-1.5"
                data-testid={`${testidPrefix}-requires-selection`}>
                <option value="pick_any">Pick any (default)</option>
                <option value="pick_one">Pick one (required)</option>
              </select>
            </label>
            <Button size="sm" variant="outline" className="rounded-sm h-7"
              onClick={onAddItem}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add item
            </Button>
            <Button size="sm" variant="ghost" className="rounded-sm h-7 text-red-600"
              onClick={onRemove}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <Textarea rows={4} value={section.description || ""}
          onChange={(e) => onPatch({ description: e.target.value })}
          placeholder="Section description shown above the table…"
          className="text-sm min-h-[88px]" />

        {/* Reference links — sample websites, example posts, demo
            dashboards. Each renders as a clickable chip on the share
            view + hyperlink on the PDF (opens new tab). */}
        <div className="border border-zinc-200 rounded-sm p-3 space-y-2 bg-zinc-50/40">
          <div className="flex items-center justify-between gap-2">
            <Label className="label-overline text-[10px]">
              Reference links (open in new tab on client view)
            </Label>
            <Button size="sm" variant="outline" className="rounded-sm h-7"
              onClick={() => onPatch({
                sample_links: [...(section.sample_links || []),
                               { label: "", url: "" }],
              })}
              data-testid={`${testidPrefix}-add-link`}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add link
            </Button>
          </div>
          {(section.sample_links || []).length === 0 ? (
            <p className="text-[11px] text-zinc-500 italic">
              No sample links yet — add reference URLs (e.g. sample websites
              you&apos;ve built) so the client can click through and review.
            </p>
          ) : (
            <div className="space-y-1.5">
              {(section.sample_links || []).map((lk, li) => (
                <div key={`samplelink_${li}_${lk.url || lk.label || ""}`} className="grid grid-cols-12 gap-2 items-center"
                  data-testid={`${testidPrefix}-link-${li}`}>
                  <Input value={lk.label || ""} placeholder="Label (e.g. Image Traders)"
                    onChange={(e) => {
                      const next = [...(section.sample_links || [])];
                      next[li] = { ...next[li], label: e.target.value };
                      onPatch({ sample_links: next });
                    }}
                    className="col-span-4 h-8 text-xs"
                    data-testid={`${testidPrefix}-link-${li}-label`} />
                  <Input type="url" value={lk.url || ""}
                    placeholder="https://example.com.au"
                    onChange={(e) => {
                      const next = [...(section.sample_links || [])];
                      next[li] = { ...next[li], url: e.target.value };
                      onPatch({ sample_links: next });
                    }}
                    className="col-span-7 h-8 text-xs"
                    data-testid={`${testidPrefix}-link-${li}-url`} />
                  <Button size="sm" variant="ghost"
                    className="col-span-1 h-8 px-1 text-red-600"
                    onClick={() => {
                      const next = (section.sample_links || []).filter(
                        (_, k) => k !== li,
                      );
                      onPatch({ sample_links: next });
                    }}
                    data-testid={`${testidPrefix}-link-${li}-remove`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="text-[10px] text-zinc-500 italic flex items-center gap-1.5">
          <GripVertical className="h-3 w-3" />
          Drag an item onto another to group them as a single-choice block
          (radio buttons on the client view). Drop into empty space to
          un-group.
        </div>

        {section.items.length === 0 ? (
          <div className="text-[12px] text-zinc-500 italic">No line items — click Add item.</div>
        ) : (
          <SortableList
            ids={section.items.map((it, i) => it.id || `item_${i}`)}
            onReorder={(from, to) => onReorderItem && onReorderItem(from, to)}>
          <div className="space-y-2"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              // Drop on container background (not on a row) → clear group.
              e.preventDefault();
              if (!dragId) return;
              const i = findIdx(dragId);
              if (i >= 0 && section.items[i].choice_group) clearGroup(i);
              setDragId(null);
            }}>
            {section.items.map((it, iIdx) => {
              const palette = groupPalette(section, it.choice_group);
              return (
              <SortableItem key={it.id || iIdx} id={it.id || `item_${iIdx}`}>
                {({ handleProps, isDragging: dndDragging }) => (
              <div
                draggable
                onDragStart={(e) => {
                  // Native HTML5 drag is reserved for choice-group merging.
                  // dnd-kit handle uses pointer events on the GripVertical
                  // button so the two gestures don't collide.
                  setDragId(it.id);
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", it.id || "");
                }}
                onDragEnd={() => setDragId(null)}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  if (dragId && dragId !== it.id) {
                    mergeIntoGroup(dragId, it.id);
                  }
                  setDragId(null);
                }}
                className={`grid grid-cols-12 gap-2 items-start p-2 rounded-sm border border-l-4 ${
                  palette ? palette.border : "border-l-transparent"
                } ${
                  it.included && it.qty > 0
                    ? "border-zinc-200 bg-white"
                    : "border-zinc-100 bg-zinc-50/60 opacity-70"
                } ${dragId === it.id ? "opacity-40" : ""} ${
                  dndDragging ? "ring-2 ring-zinc-300 shadow" : ""
                }`}
                data-testid={`${testidPrefix}-item-${iIdx}`}>
                <div className="col-span-1 flex flex-col items-center justify-start pt-1 gap-1">
                  <button type="button" {...handleProps}
                    className="cursor-grab active:cursor-grabbing touch-none p-0.5 text-zinc-400 hover:text-zinc-700 rounded-sm"
                    title="Drag to reorder line item"
                    aria-label="Drag to reorder line item"
                    data-testid={`${testidPrefix}-item-${iIdx}-drag-handle`}>
                    <GripVertical className="h-3.5 w-3.5" />
                  </button>
                  <input type="checkbox" checked={!!it.included}
                    onChange={(e) => onPatchItem(iIdx, { included: e.target.checked })}
                    title="Include in proposal"
                    data-testid={`${testidPrefix}-item-${iIdx}-included`} />
                </div>
                <div className="col-span-4">
                  <Input value={it.title} placeholder="Title"
                    onChange={(e) => onPatchItem(iIdx, { title: e.target.value })}
                    className="h-8 text-sm" />
                </div>
                <div className="col-span-1">
                  <Input type="number" step="0.5" value={it.qty}
                    onChange={(e) => onPatchItem(iIdx, { qty: Number(e.target.value) })}
                    className="h-8 text-sm text-right"
                    data-testid={`${testidPrefix}-item-${iIdx}-qty`} />
                </div>
                <div className="col-span-1">
                  <select value={it.unit}
                    onChange={(e) => onPatchItem(iIdx, { unit: e.target.value })}
                    className="h-8 text-xs rounded-sm border border-zinc-200 px-1 w-full">
                    {["each", "hour", "day", "month", "quarter", "year", "page", "post"].map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <Input type="number" step="0.01" value={it.unit_price}
                    onChange={(e) => onPatchItem(iIdx, { unit_price: Number(e.target.value) })}
                    className="h-8 text-sm text-right" />
                </div>
                <div className="col-span-2">
                  <select value={it.recurring}
                    onChange={(e) => onPatchItem(iIdx, { recurring: e.target.value })}
                    className="h-8 text-xs rounded-sm border border-zinc-200 px-1 w-full">
                    <option value="one_off">One-off</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="biennial">Biennial</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
                <div className="col-span-1 flex items-center justify-end gap-1">
                  <span className="font-mono text-xs text-zinc-700">
                    {fmtMoney((it.qty || 0) * (it.unit_price || 0), currency)}
                  </span>
                  <Button size="sm" variant="ghost" className="h-7 px-1 text-red-600"
                    onClick={() => onRemoveItem(iIdx)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>

                {/* Full-width description + notes — span every column under
                    the title/qty/unit row so the editor can see the whole
                    paragraph at a glance. col-start-2 leaves the drag/include
                    column free as a visual gutter. */}
                <div className="col-start-2 col-span-11">
                  <Textarea value={it.description || ""} placeholder="Short description"
                    onChange={(e) => onPatchItem(iIdx, { description: e.target.value })}
                    rows={3}
                    className="w-full text-[12px] text-zinc-700 leading-snug min-h-[64px] resize-y"
                    data-testid={`${testidPrefix}-item-${iIdx}-description`} />
                </div>
                {it.notes !== undefined && (
                  <div className="col-start-2 col-span-11">
                    <Textarea value={it.notes || ""} placeholder="Notes / conditions"
                      onChange={(e) => onPatchItem(iIdx, { notes: e.target.value })}
                      rows={2}
                      className="w-full text-[11px] italic text-zinc-500 leading-snug min-h-[48px] resize-y"
                      data-testid={`${testidPrefix}-item-${iIdx}-notes`} />
                  </div>
                )}

                {/* Required + group controls — own row */}
                <div className="col-start-2 col-span-11 flex items-center gap-3 flex-wrap pt-0.5">
                  <label className="inline-flex items-center gap-1 text-[10px] text-zinc-600 cursor-pointer"
                    title="Lock as compulsory on the client share view">
                    <input type="checkbox" checked={!!it.required}
                      onChange={(e) => onPatchItem(iIdx, { required: e.target.checked })}
                      data-testid={`${testidPrefix}-item-${iIdx}-required`} />
                    <Lock className="h-3 w-3" />
                    Required
                  </label>
                  {/* Recommended = pre-tick on the client view. For
                      requires_selection / choice_group sections it
                      decides which option wins the radio; for plain
                      standalone items it pre-ticks the checkbox. */}
                  <label className="inline-flex items-center gap-1 text-[10px] text-amber-700 cursor-pointer"
                    title="Pre-select this on the client share view (client can still un-tick)">
                    <input type="checkbox" checked={!!it.recommended}
                      onChange={(e) => onPatchItem(iIdx, {
                        recommended: e.target.checked,
                      })}
                      data-testid={`${testidPrefix}-item-${iIdx}-recommended`} />
                    <Star className={`h-3 w-3 ${it.recommended ? "fill-amber-500 text-amber-500" : ""}`} />
                    Recommended
                  </label>
                  {/* Hide = completely omit this item from the URL +
                      PDF for this proposal without deleting the row.
                      Excluded from totals as well. */}
                  <label className="inline-flex items-center gap-1 text-[10px] text-zinc-500 cursor-pointer"
                    title="Hide this item from the client URL and PDF (still kept in the editor)">
                    <input type="checkbox" checked={!!it.hidden}
                      onChange={(e) => onPatchItem(iIdx, {
                        hidden: e.target.checked,
                      })}
                      data-testid={`${testidPrefix}-item-${iIdx}-hidden`} />
                    <EyeOff className={`h-3 w-3 ${it.hidden ? "text-zinc-700" : ""}`} />
                    Hide
                  </label>
                  {it.choice_group ? (
                    <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${palette ? `${palette.bg} ${palette.fg}` : "bg-zinc-100 text-zinc-700"}`}
                      data-testid={`${testidPrefix}-item-${iIdx}-group-chip`}>
                      {groupLabel(section, it.choice_group)}
                      <button type="button"
                        onClick={() => clearGroup(iIdx)}
                        title="Remove from group"
                        className="ml-0.5 hover:opacity-70"
                        data-testid={`${testidPrefix}-item-${iIdx}-group-clear`}>
                        <Unlink className="h-2.5 w-2.5" />
                      </button>
                    </span>
                  ) : (
                    <span className="text-[10px] text-zinc-400 italic">
                      drag onto another item to group
                    </span>
                  )}
                </div>
              </div>
                )}
              </SortableItem>
              );
            })}
          </div>
          </SortableList>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={`space-y-1.5 ${full ? "md:col-span-2" : ""}`}>
      <Label className="label-overline text-[10px]">{label}</Label>
      {children}
    </div>
  );
}

function Total({ label, v, cur, sub, highlight }) {
  return (
    <div className={highlight ? "border-l border-zinc-700 pl-4" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</div>
      <div className={`font-display font-extrabold ${highlight ? "text-2xl text-white" : "text-xl text-white"} mt-1`}>
        {fmtMoney(v, cur)}
      </div>
      {sub !== undefined && (
        <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
          {typeof sub === "number" ? `inc GST ${fmtMoney(sub, cur)}` : sub}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Agency manager — multi-brand sender support (Iceberg Digital, Image Traders, ...)
// ──────────────────────────────────────────────────────────────────────────

function AgencyManagerDialog({ open, onOpenChange, agencies, onChange }) {
  const [draft, setDraft] = useState(null);   // null = picker; obj = editing
  const [saving, setSaving] = useState(false);

  const reset = () => setDraft(null);

  useEffect(() => { if (!open) reset(); }, [open]);

  const startNew = () => setDraft({
    name: "", logo_url: "", address: "", abn: "",
    email: "", phone: "", website: "", is_default: agencies.length === 0,
  });

  const edit = (a) => setDraft({ ...a });

  const save = async () => {
    if (!draft.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const id = draft.agency_profile_id;
      const body = {
        name: draft.name, logo_url: draft.logo_url || "",
        address: draft.address || "", abn: draft.abn || "",
        email: draft.email || "", phone: draft.phone || "",
        website: draft.website || "", is_default: !!draft.is_default,
      };
      const { data } = id
        ? await api.put(`/agency-profiles/${id}`, body)
        : await api.post(`/agency-profiles`, body);
      const list = await api.get(`/agency-profiles`);
      onChange(list.data?.agency_profiles || []);
      toast.success(id ? "Saved" : `Added ${data.name}`);
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this sender agency?")) return;
    try {
      await api.delete(`/agency-profiles/${id}`);
      const list = await api.get(`/agency-profiles`);
      onChange(list.data?.agency_profiles || []);
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="agency-manager-dialog">
        <DialogHeader>
          <DialogTitle>Manage sender agencies</DialogTitle>
          <DialogDescription>
            Register the different agency identities you want to send
            proposals from (e.g. Iceberg Digital + Image Traders). Pick
            one per proposal from the dropdown in the editor.
          </DialogDescription>
        </DialogHeader>

        {draft ? (
          <div className="space-y-3">
            <Field label="Agency name *">
              <Input value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                data-testid="agency-name-input" />
            </Field>
            <Field label="Logo URL (PNG/JPG, displayed on PDF + share view)">
              <Input value={draft.logo_url}
                placeholder="https://…/logo.png"
                onChange={(e) => setDraft({ ...draft, logo_url: e.target.value })}
                data-testid="agency-logo-input" />
              {draft.logo_url && (
                <img src={draft.logo_url} alt=""
                  className="mt-2 h-12 object-contain border border-zinc-100 rounded-sm bg-white p-1" />
              )}
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email">
                <Input value={draft.email}
                  onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
              </Field>
              <Field label="Phone">
                <Input value={draft.phone}
                  onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
              </Field>
              <Field label="Website">
                <Input value={draft.website}
                  onChange={(e) => setDraft({ ...draft, website: e.target.value })} />
              </Field>
              <Field label="ABN">
                <Input value={draft.abn}
                  onChange={(e) => setDraft({ ...draft, abn: e.target.value })} />
              </Field>
            </div>
            <Field label="Postal / business address">
              <Input value={draft.address}
                onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!draft.is_default}
                onChange={(e) => setDraft({ ...draft, is_default: e.target.checked })} />
              Default sender — used on new proposals
            </label>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={reset}>Cancel</Button>
              <Button onClick={save} disabled={saving}
                className="rounded-sm bg-[#002FA7] hover:bg-[#001f70] text-white"
                data-testid="agency-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-2">
            {agencies.length === 0 ? (
              <div className="text-[12px] text-zinc-500 italic">
                No sender agencies yet — click <strong>Add agency</strong>.
              </div>
            ) : (
              agencies.map((a) => (
                <div key={a.agency_profile_id}
                  className="flex items-center gap-3 border border-zinc-200 rounded-sm p-3"
                  data-testid={`agency-row-${a.agency_profile_id}`}>
                  {a.logo_url
                    ? <img src={a.logo_url} alt="" className="h-8 w-8 object-contain border border-zinc-100 rounded-sm bg-white p-0.5" />
                    : <div className="h-8 w-8 grid place-items-center bg-zinc-100 rounded-sm text-[10px] font-mono text-zinc-500">—</div>}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">
                      {a.name}
                      {a.is_default && <span className="ml-2 text-[10px] uppercase font-mono text-emerald-700">default</span>}
                    </div>
                    <div className="text-[11px] font-mono text-zinc-500 truncate">
                      {a.email || ""} {a.email && a.phone ? " · " : ""}{a.phone || ""}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-sm h-7"
                    onClick={() => edit(a)}>Edit</Button>
                  <Button size="sm" variant="ghost" className="rounded-sm h-7 text-red-600"
                    onClick={() => remove(a.agency_profile_id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))
            )}
            <DialogFooter>
              <Button onClick={startNew} className="rounded-sm bg-[#002FA7] hover:bg-[#001f70] text-white"
                data-testid="agency-add-btn">
                <Plus className="h-4 w-4 mr-2" /> Add agency
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SendDialog({ open, onOpenChange, contacts, proposal, proposalId, onSent }) {
  const [emails, setEmails] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [intro, setIntro] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Pre-fill from primary contact if available
    const primary = contacts.find((c) => c.is_primary) || contacts[0];
    setEmails(primary?.email || "");
    setCc("");
    setSubject(`${proposal?.title || "Proposal"} — ${proposal?.prepared_by || ""}`);
    setIntro(
      `Hi ${proposal?.prepared_for || "there"},\n\n` +
      "Please find attached the proposal we discussed. You'll also find a " +
      "live online version linked below — feel free to reach out with any " +
      "questions or to schedule a follow-up call.\n",
    );
  }, [open, contacts, proposal]);

  const submit = async () => {
    const to_emails = emails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    const cc_emails = cc.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (to_emails.length === 0) { toast.error("Enter at least one recipient"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/proposals/${proposalId}/send`, {
        to_emails, cc_emails, subject, intro_message: intro,
      });
      onOpenChange(false);
      onSent(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="proposal-send-dialog">
        <DialogHeader>
          <DialogTitle>Send proposal</DialogTitle>
          <DialogDescription>
            The PDF + a shareable web link are emailed to the recipients via
            SendGrid. The proposal moves to &quot;sent&quot; status automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="To (comma-separated)">
            <Input value={emails} onChange={(e) => setEmails(e.target.value)}
              data-testid="proposal-send-to-input" />
            {contacts.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {contacts.slice(0, 6).map((c) => (
                  <button key={c.email} type="button"
                    onClick={() => setEmails((v) => v ? `${v}, ${c.email}` : c.email)}
                    className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-zinc-200 bg-zinc-50 hover:bg-zinc-100">
                    + {c.email}
                  </button>
                ))}
              </div>
            )}
          </Field>
          <Field label="CC (optional)">
            <Input value={cc} onChange={(e) => setCc(e.target.value)} />
          </Field>
          <Field label="Subject">
            <Input value={subject} onChange={(e) => setSubject(e.target.value)}
              data-testid="proposal-send-subject-input" />
          </Field>
          <Field label="Intro message">
            <Textarea rows={6} value={intro} onChange={(e) => setIntro(e.target.value)}
              data-testid="proposal-send-intro-input" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={sending}
            className="rounded-sm bg-[#002FA7] hover:bg-[#001f70] text-white"
            data-testid="proposal-send-confirm-btn">
            {sending
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Send className="h-4 w-4 mr-2" />}
            Send via SendGrid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
